import { useState } from 'react'
import { theme } from '../theme'
import { Ic } from '../icons'
import { WeekView, MonthView } from './CalendarViews'
import { SlotCard } from './SlotCard'
import { ModalShell } from './ModalShell'
import { AlertDialog } from './Dialog'
import { api, PlanWeekNeedsDisambiguationError, PlanWeekNeedsDuplicateConfirmError } from '../api'
import {
  DOW_LONG, DOW_SHORT, SLOT_KEYS, addDays, formatWeekRange, isoDate,
  monthGridStart, startOfMonth, startOfWeekMon,
} from '../dates'
import type { Plan, Recipe, SlotKey, PlanWeekMode, PlanWeekNeedsDisambiguation, PlanWeekNeedsDuplicateConfirm } from '../types'

type ViewMode = 'week' | 'month'

// usePlanWeek centralizes the two-stage plan_week protocol shared by the
// desktop and mobile tabs: a first call with no mode, which may come back as a
// disambiguation conflict (existing meals) or a forced-duplicate confirmation
// (too few recipes for the slot type). dupMode remembers the mode chosen during
// disambiguation so the force=true retry re-applies it.
function usePlanWeek(weekStart: Date) {
  const [planning, setPlanning] = useState(false)
  const [conflict, setConflict] = useState<PlanWeekNeedsDisambiguation | null>(null)
  const [dupConfirm, setDupConfirm] = useState<PlanWeekNeedsDuplicateConfirm | null>(null)
  const [dupMode, setDupMode] = useState<PlanWeekMode | undefined>(undefined)
  const [planError, setPlanError] = useState<string | null>(null)

  const runPlanWeek = async (mode?: PlanWeekMode, force?: boolean) => {
    if (planning) return
    setPlanning(true)
    try {
      await api.planWeek(isoDate(weekStart), mode, force)
      setConflict(null)
      setDupConfirm(null)
    } catch (e) {
      if (e instanceof PlanWeekNeedsDisambiguationError) {
        setDupConfirm(null)
        setConflict(e.envelope)
      } else if (e instanceof PlanWeekNeedsDuplicateConfirmError) {
        setConflict(null)
        setDupMode(mode)
        setDupConfirm(e.envelope)
      } else {
        console.error(e)
        setPlanError(e instanceof Error ? e.message : 'Plan failed')
      }
    } finally {
      setPlanning(false)
    }
  }

  return { planning, conflict, setConflict, dupConfirm, setDupConfirm, dupMode, planError, setPlanError, runPlanWeek }
}

export function PlanTabDesktop({
  today, plan, recipesById, onSlotClick, onSlotClear,
}: {
  today: Date
  plan: Plan
  recipesById: Map<number, Recipe>
  onSlotClick: (date: string, slot: SlotKey) => void
  onSlotClear?: (date: string, slot: SlotKey) => void
}) {
  const [view, setView] = useState<ViewMode>('week')
  const [weekStart, setWeekStart] = useState(startOfWeekMon(today))
  const [monthDate, setMonthDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const {
    planning, conflict, setConflict, dupConfirm, setDupConfirm, dupMode, planError, setPlanError, runPlanWeek,
  } = usePlanWeek(weekStart)

  const headerTitle = view === 'week'
    ? formatWeekRange(weekStart)
    : monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  const onPrev = () => view === 'week'
    ? setWeekStart(d => addDays(d, -7))
    : setMonthDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))
  const onNext = () => view === 'week'
    ? setWeekStart(d => addDays(d, 7))
    : setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  const onToday = () => {
    setWeekStart(startOfWeekMon(today))
    setMonthDate(new Date(today.getFullYear(), today.getMonth(), 1))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>Meal plan</div>
          <h1 style={{
            fontFamily: theme.serif, fontSize: 38, fontWeight: theme.headWeight,
            color: theme.ink, margin: '2px 0 0', lineHeight: 1.0, whiteSpace: 'nowrap',
          }}>{headerTitle}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {view === 'week' && (
            <button
              onClick={() => runPlanWeek()}
              disabled={planning}
              style={{
                all: 'unset', cursor: planning ? 'default' : 'pointer',
                padding: '6px 14px', borderRadius: theme.radius,
                background: theme.ink, color: theme.surface,
                fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase', opacity: planning ? 0.6 : 1,
              }}
              title="Fill this week's planner-enabled future slots with recipes from your cookbook"
            >
              {planning ? 'Planning…' : 'Plan week'}
            </button>
          )}
          <ViewToggle view={view} setView={setView}/>
          <div style={{ width: 1, height: 22, background: theme.line }}/>
          <NavBtn onClick={onPrev}><Ic.chevL width="16" height="16"/></NavBtn>
          <button onClick={onToday} style={{
            all: 'unset', cursor: 'pointer',
            padding: '6px 14px', borderRadius: theme.radius,
            border: `1px solid ${theme.lineStrong}`,
            fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
            textTransform: 'uppercase', color: theme.ink,
          }}>Today</button>
          <NavBtn onClick={onNext}><Ic.chevR width="16" height="16"/></NavBtn>
        </div>
      </div>

      <PlanConflictModal
        env={conflict}
        busy={planning}
        onChoose={mode => runPlanWeek(mode)}
        onCancel={() => setConflict(null)}
      />
      <PlanDuplicateModal
        env={dupConfirm}
        busy={planning}
        onConfirm={() => runPlanWeek(dupMode, true)}
        onCancel={() => setDupConfirm(null)}
      />
      <AlertDialog
        open={planError !== null}
        message={planError ?? ''}
        onClose={() => setPlanError(null)}
      />

      {view === 'week'
        ? <WeekView weekStart={weekStart} today={today} plan={plan} recipesById={recipesById} onSlotClick={onSlotClick} onSlotClear={onSlotClear}/>
        : <MonthView monthDate={monthDate} today={today} plan={plan} recipesById={recipesById} onSlotClick={onSlotClick}/>}
    </div>
  )
}

function NavBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      all: 'unset', cursor: 'pointer',
      width: 32, height: 32, borderRadius: theme.radius,
      display: 'grid', placeItems: 'center',
      border: `1px solid ${theme.lineStrong}`, color: theme.ink,
    }}>{children}</button>
  )
}

function ViewToggle({ view, setView }: { view: ViewMode; setView: (v: ViewMode) => void }) {
  return (
    <div style={{
      display: 'inline-flex', border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
      padding: 2, background: theme.surface,
    }}>
      {(['week','month'] as ViewMode[]).map(v => (
        <button key={v} onClick={() => setView(v)} style={{
          all: 'unset', cursor: 'pointer',
          padding: '6px 14px', borderRadius: theme.radius - 1,
          fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
          textTransform: 'uppercase',
          background: view === v ? theme.ink : 'transparent',
          color: view === v ? theme.surface : theme.inkSoft,
        }}>{v === 'week' ? 'Week' : 'Month'}</button>
      ))}
    </div>
  )
}

export function PlanTabMobile({
  today, plan, recipesById, onSlotClick, onSlotClear,
}: {
  today: Date
  plan: Plan
  recipesById: Map<number, Recipe>
  onSlotClick: (date: string, slot: SlotKey) => void
  onSlotClear?: (date: string, slot: SlotKey) => void
}) {
  const [view, setView] = useState<ViewMode>('week')
  const [weekStart, setWeekStart] = useState(startOfWeekMon(today))
  const [selectedIso, setSelectedIso] = useState(isoDate(today))
  const {
    planning, conflict, setConflict, dupConfirm, setDupConfirm, dupMode, planError, setPlanError, runPlanWeek,
  } = usePlanWeek(weekStart)

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const selectedDate = new Date(selectedIso + 'T00:00:00')
  const selectedPlan = plan[selectedIso]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 9, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>{selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
          <h1 style={{
            fontFamily: theme.serif, fontSize: 30, fontWeight: theme.headWeight,
            color: theme.ink, margin: '2px 0 0', lineHeight: 1.05,
          }}>{DOW_LONG[(selectedDate.getDay() + 6) % 7]} {selectedDate.getDate()}</h1>
        </div>
        <ViewToggle view={view} setView={setView}/>
      </div>

      {view === 'week' && <>
        <div style={{
          display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'space-between',
          fontFamily: theme.sans, fontSize: 11, color: theme.inkSoft,
        }}>
          <button onClick={() => setWeekStart(d => addDays(d, -7))} style={{
            all: 'unset', cursor: 'pointer', padding: '4px 8px',
          }}><Ic.chevL width="14" height="14"/></button>
          <span style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {formatWeekRange(weekStart)}
          </span>
          <button onClick={() => setWeekStart(d => addDays(d, 7))} style={{
            all: 'unset', cursor: 'pointer', padding: '4px 8px',
          }}><Ic.chevR width="14" height="14"/></button>
        </div>

        <button
          onClick={() => runPlanWeek()}
          disabled={planning}
          style={{
            all: 'unset', cursor: planning ? 'default' : 'pointer',
            alignSelf: 'stretch', textAlign: 'center',
            padding: '10px 14px', borderRadius: theme.radius,
            background: theme.ink, color: theme.surface,
            fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase', opacity: planning ? 0.6 : 1,
          }}
        >
          {planning ? 'Planning…' : 'Plan week'}
        </button>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
          background: theme.surface, border: theme.cardBorder, borderRadius: theme.radius, padding: 4,
        }}>
          {days.map((d, i) => {
            const iso = isoDate(d)
            const sel = iso === selectedIso
            const dp = plan[iso]
            const filled = dp ? SLOT_KEYS.filter(k => dp[k]).length : 0
            return (
              <button key={iso} onClick={() => setSelectedIso(iso)} style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                padding: '8px 0',
                background: sel ? theme.ink : 'transparent',
                borderRadius: theme.radius - 1,
              }}>
                <span style={{
                  fontFamily: theme.sans, fontSize: 9, letterSpacing: 1, fontWeight: 600,
                  color: sel ? theme.surface : theme.muted,
                }}>{DOW_LONG[i]}</span>
                <span style={{
                  fontFamily: theme.serif, fontSize: 18, fontWeight: theme.headWeight,
                  color: sel ? theme.surface : theme.ink, lineHeight: 1,
                }}>{d.getDate()}</span>
                <span style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                  {[0,1,2].map(j => (
                    <span key={j} style={{
                      width: 4, height: 4, borderRadius: 999,
                      background: j < filled
                        ? (sel ? theme.surface : theme.accent)
                        : (sel ? 'rgba(255,255,255,0.25)' : theme.line),
                    }}/>
                  ))}
                </span>
              </button>
            )
          })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          {SLOT_KEYS.map(sk => {
            const m = selectedPlan?.[sk]
            const r = m?.recipe_id ? recipesById.get(m.recipe_id) : undefined
            return <SlotCard key={sk} slotKey={sk} meal={m} recipe={r}
              onClick={() => onSlotClick(selectedIso, sk)}
              onClear={m && r && onSlotClear ? () => onSlotClear(selectedIso, sk) : undefined}/>
          })}
        </div>
      </>}

      {view === 'month' && (
        <MonthMini today={today} plan={plan} selectedIso={selectedIso} setSelectedIso={setSelectedIso}/>
      )}

      <PlanConflictModal
        env={conflict}
        busy={planning}
        onChoose={mode => runPlanWeek(mode)}
        onCancel={() => setConflict(null)}
      />
      <PlanDuplicateModal
        env={dupConfirm}
        busy={planning}
        onConfirm={() => runPlanWeek(dupMode, true)}
        onCancel={() => setDupConfirm(null)}
      />
      <AlertDialog
        open={planError !== null}
        message={planError ?? ''}
        onClose={() => setPlanError(null)}
      />
    </div>
  )
}

function MonthMini({ today, plan, selectedIso, setSelectedIso }: {
  today: Date; plan: Plan; selectedIso: string; setSelectedIso: (iso: string) => void
}) {
  const monthDate = new Date(selectedIso + 'T00:00:00')
  const gridStart = monthGridStart(startOfMonth(monthDate))
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i))
  const month = monthDate.getMonth()
  const todayIso = isoDate(today)

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 6 }}>
        {DOW_SHORT.map((d, i) => (
          <div key={i} style={{
            fontFamily: theme.sans, fontSize: 9, letterSpacing: 1.2, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted, textAlign: 'center',
          }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: 44, gap: 2 }}>
        {days.map(d => {
          const iso = isoDate(d)
          const inMonth = d.getMonth() === month
          const isToday = iso === todayIso
          const sel = iso === selectedIso
          const dp = plan[iso]
          const filled = dp ? SLOT_KEYS.filter(k => dp[k]).length : 0
          return (
            <button key={iso} onClick={() => setSelectedIso(iso)} style={{
              all: 'unset', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
              borderRadius: theme.radius,
              background: sel ? theme.ink : 'transparent',
              opacity: inMonth ? 1 : 0.35,
              border: isToday && !sel ? `1px solid ${theme.accent}` : '1px solid transparent',
            }}>
              <span style={{
                fontFamily: theme.serif, fontSize: 15, fontWeight: theme.headWeight,
                color: sel ? theme.surface : (isToday ? theme.accent : theme.ink), lineHeight: 1,
              }}>{d.getDate()}</span>
              <span style={{ display: 'flex', gap: 2 }}>
                {[0,1,2].map(j => (
                  <span key={j} style={{
                    width: 3, height: 3, borderRadius: 999,
                    background: j < filled ? (sel ? theme.surface : theme.accent) : 'transparent',
                  }}/>
                ))}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function PlanConflictModal({ env, busy, onChoose, onCancel }: {
  env: PlanWeekNeedsDisambiguation | null
  busy: boolean
  onChoose: (mode: PlanWeekMode) => void
  onCancel: () => void
}) {
  const open = env !== null
  const n = env?.conflicts.length ?? 0
  return (
    <ModalShell
      open={open} onClose={onCancel} zIndex={80} autoHeight
      panelStyle={{ width: 'min(440px, 100%)', padding: '22px 24px', gap: 14 }}
    >
      <div style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>Plan week</div>
      <div style={{
        fontFamily: theme.serif, fontSize: 20, fontWeight: theme.headWeight,
        color: theme.ink, lineHeight: 1.35,
      }}>
        This week already has {n} planned meal{n === 1 ? '' : 's'}.
      </div>
      <div style={{ fontSize: 13, color: theme.inkSoft, lineHeight: 1.5 }}>
        Locked meals are always kept. Choose what to do with the rest.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
        <button
          onClick={() => onChoose('overwrite_future')}
          disabled={busy}
          style={{
            all: 'unset', cursor: busy ? 'default' : 'pointer',
            padding: '10px 14px', borderRadius: theme.radius,
            background: theme.ink, color: theme.surface, textAlign: 'center',
            fontFamily: theme.sans, fontSize: 13, fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase', opacity: busy ? 0.6 : 1,
          }}
        >Overwrite future plans</button>
        <button
          onClick={() => onChoose('fill_empty_only')}
          disabled={busy}
          style={{
            all: 'unset', cursor: busy ? 'default' : 'pointer',
            padding: '10px 14px', borderRadius: theme.radius,
            background: theme.surface, color: theme.ink, textAlign: 'center',
            border: `1px solid ${theme.lineStrong}`,
            fontFamily: theme.sans, fontSize: 13, fontWeight: 600, letterSpacing: 0.4,
            textTransform: 'uppercase', opacity: busy ? 0.6 : 1,
          }}
        >Only fill empty slots</button>
        <button
          onClick={onCancel}
          disabled={busy}
          style={{
            all: 'unset', cursor: busy ? 'default' : 'pointer',
            padding: '8px 14px', borderRadius: theme.radius,
            color: theme.inkSoft, textAlign: 'center',
            fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >Cancel</button>
      </div>
    </ModalShell>
  )
}

// PlanDuplicateModal warns that a slot type has more days to fill than there are
// distinct eligible recipes, so planning now will repeat recipes within the
// week. Confirming re-runs plan_week with force=true.
function PlanDuplicateModal({ env, busy, onConfirm, onCancel }: {
  env: PlanWeekNeedsDuplicateConfirm | null
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const open = env !== null
  const dups = env?.duplicates ?? []
  return (
    <ModalShell
      open={open} onClose={onCancel} zIndex={80} autoHeight
      panelStyle={{ width: 'min(440px, 100%)', padding: '22px 24px', gap: 14 }}
    >
      <div style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>Plan week</div>
      <div style={{
        fontFamily: theme.serif, fontSize: 20, fontWeight: theme.headWeight,
        color: theme.ink, lineHeight: 1.35,
      }}>
        Not enough recipes to avoid repeats.
      </div>
      <div style={{ fontSize: 13, color: theme.inkSoft, lineHeight: 1.5 }}>
        Some slots have more days to fill than recipes that fit them, so the planner
        will reuse recipes within the week:
      </div>
      <ul style={{
        margin: 0, paddingLeft: 18, fontSize: 13, color: theme.ink, lineHeight: 1.6,
      }}>
        {dups.map(d => (
          <li key={d.slot}>
            <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{d.slot}</span>
            {` — ${d.slots_to_fill} days, ${d.available_recipes} recipe${d.available_recipes === 1 ? '' : 's'}`}
          </li>
        ))}
      </ul>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
        <button
          onClick={onConfirm}
          disabled={busy}
          style={{
            all: 'unset', cursor: busy ? 'default' : 'pointer',
            padding: '10px 14px', borderRadius: theme.radius,
            background: theme.ink, color: theme.surface, textAlign: 'center',
            fontFamily: theme.sans, fontSize: 13, fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase', opacity: busy ? 0.6 : 1,
          }}
        >Plan with duplicates</button>
        <button
          onClick={onCancel}
          disabled={busy}
          style={{
            all: 'unset', cursor: busy ? 'default' : 'pointer',
            padding: '8px 14px', borderRadius: theme.radius,
            color: theme.inkSoft, textAlign: 'center',
            fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >Cancel</button>
      </div>
    </ModalShell>
  )
}
